import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Eye, EyeOff, AlertCircle, CheckCircle, ArrowRight, ChevronDown } from 'lucide-react';
import { VaultGrowLogo } from '@/components/VaultGrowLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COUNTRY_CODES = [
  { code: '+593', country: 'Ecuador' },
  { code: '+57', country: 'Colombia' },
  { code: '+51', country: 'Perú' },
  { code: '+56', country: 'Chile' },
  { code: '+54', country: 'Argentina' },
  { code: '+52', country: 'México' },
  { code: '+1', country: 'Estados Unidos / Canadá' },
  { code: '+34', country: 'España' },
  { code: '+58', country: 'Venezuela' },
  { code: '+591', country: 'Bolivia' },
  { code: '+595', country: 'Paraguay' },
  { code: '+598', country: 'Uruguay' },
  { code: '+507', country: 'Panamá' },
  { code: '+506', country: 'Costa Rica' },
  { code: '+502', country: 'Guatemala' },
  { code: '+503', country: 'El Salvador' },
  { code: '+504', country: 'Honduras' },
  { code: '+505', country: 'Nicaragua' },
];

const C = {
  bg: '#131413',
  accent: '#9EAFAC',
  accentDark: '#586060',
  border: 'rgba(158,175,172,0.15)',
  card: 'rgba(19,20,19,0.85)',
};

export default function LoginPage() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState('login');
  const [refCode, setRefCode] = useState(searchParams.get('ref') || '');
  const [refValid, setRefValid] = useState(null);
  const [refName, setRefName] = useState('');
  const [form, setForm] = useState({ country_code: '+593', phone_number: '', full_name: '', password: '' });
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

const validateRef = useCallback(async (code) => {
  const cleanCode = code.trim().toUpperCase();

  if (!cleanCode || cleanCode.length < 4) {
    setRefValid(false);
    return;
  }

  try {
    const res = await axios.get(`${API}/auth/validate-ref/${cleanCode}`);
    setRefValid(true);
    setRefName(res.data.referrer_name);
  } catch {
    setRefValid(false);
    setRefName('');
  }
}, []);

  useEffect(() => {
   const ref = searchParams.get('ref');
if (ref) {
  const cleanRef = ref.trim().toUpperCase();
  setMode('register');
  setRefCode(cleanRef);
  validateRef(cleanRef);
}
  }, [searchParams, validateRef]);

  const handleRefChange = (val) => {
    const upper = val.trim().toUpperCase();
    setRefCode(upper);
    if (upper.length >= 4) validateRef(upper);
    else setRefValid(null);
  };

  const filteredCountryCodes = COUNTRY_CODES.filter((item) => {
    const q = countrySearch.trim().toLowerCase();
    if (!q) return true;
    return item.country.toLowerCase().includes(q) || item.code.includes(q.replace(/\s+/g, ''));
  });

  const selectedCountry = COUNTRY_CODES.find((item) => item.code === form.country_code) || COUNTRY_CODES[0];
  const isEcuador = form.country_code === '+593';

  // Format phone display for Ecuador: 3-3-4 (e.g. "000 000 0000")
  const formatEcuadorPhone = (digits) => {
    const d = digits.replace(/\D/g, '');
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`;
    return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (isEcuador) {
      const limited = raw.slice(0, 10);
      setPhoneDisplay(formatEcuadorPhone(limited));
      // Store without 0 prefix for backend (backend strips leading 0 now)
      setForm(prev => ({ ...prev, phone_number: limited }));
    } else {
      setPhoneDisplay(raw);
      setForm(prev => ({ ...prev, phone_number: raw }));
    }
  };

  const handleSelectCountry = (code) => {
    setForm((prev) => ({ ...prev, country_code: code, phone_number: '' }));
    setPhoneDisplay('');
    setCountrySearch('');
    setShowCountryDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(form.country_code, form.phone_number, form.password);
      } else {
        if (!refValid) { setError('Ingresa un código de referido válido'); setLoading(false); return; }
        await register(form.country_code, form.phone_number, form.full_name, form.password, refCode);
      }
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al procesar. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (extra = {}) => ({
    width: '100%',
    background: 'rgba(158,175,172,0.04)',
    border: `1px solid ${C.border}`,
    borderRadius: '10px',
    padding: '12px 16px',
    color: 'white',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'Inter, sans-serif',
    transition: 'border-color 0.2s',
    ...extra,
  });

  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: 'Inter, sans-serif' }}>

      {/* Background nebula glow */}
      <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{
          position: 'absolute', top: '10%', right: '-5%',
          width: '55%', height: '70%',
          background: 'radial-gradient(ellipse at 60% 40%, rgba(158,175,172,0.10) 0%, rgba(88,96,96,0.06) 40%, transparent 70%)',
          filter: 'blur(60px)',
          animation: 'pulse-glow 6s ease-in-out infinite',
        }} />
        <div style={{
          position: 'absolute', bottom: '5%', left: '5%',
          width: '35%', height: '40%',
          background: 'radial-gradient(ellipse, rgba(88,96,96,0.06) 0%, transparent 70%)',
          filter: 'blur(50px)',
        }} />
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.04, pointerEvents: 'none' }}>
          <defs>
            <pattern id="grid-login" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#9EAFAC" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-login)" />
        </svg>
      </div>

      <div style={{ width: '100%', maxWidth: '460px', position: 'relative', zIndex: 10 }}>
        <div style={{ textAlign: 'center', marginBottom: '40px', animation: 'fade-up 0.5s ease both' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
            <VaultGrowLogo size={44} borderRadius={12} />
            <span style={{ color: 'white', fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '28px', letterSpacing: '-0.5px' }}>VaultGrow</span>
          </div>
          <p style={{ color: C.accentDark, fontSize: '14px' }}>Plataforma de crecimiento financiero</p>
        </div>

        <div style={{
          background: C.card,
          border: `1px solid ${C.border}`,
          borderRadius: '24px',
          padding: '32px',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
          animation: 'fade-up 0.6s ease both',
          animationDelay: '0.1s',
        }}>
          <div style={{ display: 'flex', background: 'rgba(158,175,172,0.06)', borderRadius: '12px', padding: '4px', marginBottom: '28px', border: `1px solid ${C.border}` }}>
            {['login', 'register'].map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); setError(''); }}
                data-testid={`${m}-tab`}
                style={{
                  flex: 1, padding: '10px', borderRadius: '9px', border: 'none', cursor: 'pointer',
                  background: mode === m ? C.accent : 'transparent',
                  color: mode === m ? '#131413' : C.accentDark,
                  fontWeight: 700, fontSize: '14px', transition: 'all 0.25s',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                {m === 'login' ? 'Iniciar Sesión' : 'Registrarse'}
              </button>
            ))}
          </div>

          <div style={{ marginBottom: '24px' }}>
            <h2 style={{ color: 'white', fontFamily: 'Outfit, sans-serif', fontSize: '22px', fontWeight: 700, margin: '0 0 4px' }}>
              {mode === 'login' ? 'Bienvenido de vuelta' : 'Crear cuenta'}
            </h2>
            <p style={{ color: C.accentDark, fontSize: '14px', margin: 0 }}>
              {mode === 'login' ? 'Accede a tu cuenta VaultGrow' : 'Solo con código de referido válido'}
            </p>
          </div>

          <form onSubmit={handleSubmit} data-testid="auth-form">
            {mode === 'register' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: C.accent, fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '8px' }}>
                  Código de Referido *
                </label>
                <div style={{ position: 'relative' }}>
                <input
  type="text"
  value={refCode}
  onChange={e => handleRefChange(e.target.value)}
  maxLength={10}
  data-testid="referral-code-input"
  required
  style={{
    ...inputStyle({ paddingRight: '40px' }),
    borderColor: refValid === true ? C.accent : refValid === false ? '#ef4444' : C.border,
  }}
/>
                  {refValid !== null && (
                    <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                      {refValid
                        ? <CheckCircle size={16} color={C.accent} />
                        : <AlertCircle size={16} color="#ef4444" />}
                    </div>
                  )}
                </div>
                {refValid && refName && (
                  <p style={{ color: C.accent, fontSize: '12px', marginTop: '5px' }}>
                    <CheckCircle size={12} style={{ display: 'inline', marginRight: '4px' }} />
                    Código válido
                  </p>
                )}
                {refValid === false && (
                  <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '5px' }}>Código no válido</p>
                )}
              </div>
            )}

            {mode === 'register' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', color: C.accentDark, fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '8px' }}>
                  Nombre de Usuario
                </label>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={e => setForm({ ...form, full_name: e.target.value })}
                  placeholder="@ejemplo123"
                  data-testid="full-name-input"
                  required
                  maxLength={30}
                  style={inputStyle()}
                />
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', color: C.accentDark, fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '8px' }}>
                Celular
              </label>
              <div style={{ position: 'relative', marginBottom: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                  data-testid="country-code-input"
                  style={{
                    ...inputStyle({ display: 'flex', alignItems: 'center', justifyContent: 'space-between', textAlign: 'left' }),
                    cursor: 'pointer',
                  }}
                >
                  <span>{selectedCountry.code} · {selectedCountry.country}</span>
                  <ChevronDown size={16} color={C.accentDark} />
                </button>
                {showCountryDropdown && (
                  <div style={{
                    position: 'absolute', zIndex: 50, left: 0, right: 0, marginTop: '6px',
                    background: '#171917', border: `1px solid ${C.border}`, borderRadius: '12px',
                    maxHeight: '220px', overflowY: 'auto', padding: '8px',
                  }}>
                    <input
                      type="text"
                      value={countrySearch}
                      onChange={e => setCountrySearch(e.target.value)}
                      placeholder="Buscar país o prefijo"
                      style={inputStyle({ marginBottom: '8px', padding: '10px 12px' })}
                    />
                    {filteredCountryCodes.map((item) => (
                      <button
                        key={`${item.code}-${item.country}`}
                        type="button"
                        onClick={() => handleSelectCountry(item.code)}
                        style={{
                          width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: '10px',
                          border: 'none', background: form.country_code === item.code ? 'rgba(158,175,172,0.14)' : 'transparent',
                          color: 'white', cursor: 'pointer', fontSize: '14px',
                        }}
                      >
                        {item.country} ({item.code})
                      </button>
                    ))}
                    {filteredCountryCodes.length === 0 && (
                      <p style={{ color: C.accentDark, fontSize: '13px', margin: '8px 4px' }}>No se encontraron resultados.</p>
                    )}
                  </div>
                )}
              </div>
              <div style={{ position: 'relative' }}>
                <span style={{
                  position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: C.accent,
                  fontSize: '14px', fontWeight: 700, pointerEvents: 'none',
                }}>{form.country_code}</span>
                <input
                  type="tel"
                  value={phoneDisplay}
                  onChange={handlePhoneChange}
                  placeholder={isEcuador ? "000 000 0000" : "987654321"}
                  data-testid="phone-number-input"
                  required
                  maxLength={isEcuador ? 12 : 20}
                  style={inputStyle({ paddingLeft: `${Math.max(58, form.country_code.length * 10 + 26)}px` })}
                />
                {isEcuador && form.phone_number.length > 0 && form.phone_number.length < 10 && (
                  <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#d97706', fontSize: '11px' }}>
                    {10 - form.phone_number.length} dígito{10 - form.phone_number.length !== 1 ? 's' : ''} más
                  </span>
                )}
                {isEcuador && form.phone_number.length === 10 && (
                  <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#4ade80', fontSize: '11px' }}>✓</span>
                )}
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', color: C.accentDark, fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '8px' }}>
                Contraseña
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  data-testid="password-input"
                  required
                  minLength={mode === 'register' ? 6 : undefined}
                  style={inputStyle({ paddingRight: '44px' })}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: C.accentDark }}
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="auth-error" style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: '10px', padding: '12px', marginBottom: '16px',
                color: '#fc8181', fontSize: '14px',
              }}>
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            <div style={{ position: 'relative' }}>
              <button
                type="submit"
                disabled={loading}
                data-testid="auth-submit-button"
                className="btn-glow"
                style={{
                  width: '100%', padding: '14px',
                  background: loading ? C.accentDark : C.accent,
                  border: 'none', borderRadius: '12px',
                  color: '#131413', fontWeight: 800, fontSize: '16px',
                  cursor: loading ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  transition: 'all 0.2s',
                  boxShadow: `0 0 24px rgba(158,175,172,0.25)`,
                  opacity: loading ? 0.8 : 1,
                  fontFamily: 'Outfit, sans-serif',
                }}
                onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = 'white'; e.currentTarget.style.boxShadow = '0 0 40px rgba(255,255,255,0.2)'; } }}
                onMouseLeave={e => { if (!loading) { e.currentTarget.style.background = C.accent; e.currentTarget.style.boxShadow = `0 0 24px rgba(158,175,172,0.25)`; } }}
              >
                {loading ? (
                  <div style={{ width: '20px', height: '20px', border: '2px solid rgba(19,20,19,0.3)', borderTopColor: '#131413', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                ) : (
                  <>
                    {mode === 'login' ? 'Entrar a VaultGrow' : 'Crear mi Cuenta'}
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </div>
          </form>

          <p style={{ textAlign: 'center', color: C.accentDark, fontSize: '13px', marginTop: '20px' }}>
            {mode === 'login' ? '¿No tienes cuenta?' : '¿Ya tienes cuenta?'}{' '}
            <button
              onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
              style={{ color: C.accent, background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
            >
              {mode === 'login' ? 'Regístrate' : 'Inicia sesión'}
            </button>
          </p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '24px', flexWrap: 'wrap', animation: 'fade-up 0.7s ease both', animationDelay: '0.2s' }}>
          {[['$100', 'Activación'], ['$600', 'Meta del ciclo'], ['14', 'Posiciones del ciclo']].map(([v, l]) => (
            <div key={l} style={{
              background: 'rgba(158,175,172,0.04)', border: `1px solid ${C.border}`,
              borderRadius: '12px', padding: '10px 18px', textAlign: 'center',
            }}>
              <div style={{ color: C.accent, fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: '16px' }}>{v}</div>
              <div style={{ color: C.accentDark, fontSize: '11px', marginTop: '2px' }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
